import EventDispatcher from '../../events/EventDispatcher';
import { CACHE } from '../../globals';

/**
 * @class
 * @inheritDoc
 * @extends EventEmitter
 * @memberof module:media.module:loaders
 * @classdesc Элемент очереди загрузки
 */
class QueueItem extends EventDispatcher {

    constructor(...params) {
        super();

        /** @ignore */
        this.data = this.prepareData(...params);

        /** @ignore */
        this.key = this.prepareKey(...params);

        /** @ignore */
        this.name = this.key; // TODO: API

        /**
         * Функция-парсер, которая может модифицировать данные перед завершением
         * @type {Function}
         */
        this.parser = null;

        /** @ignore */
        this._complete = false;

        /** @ignore */
        this._status = null;
        /** @ignore */
        this._statusMessage = null;
    }

    destructor(){
        super.destructor();
        this.data = null;
    }

    /**
     * @type {module:media.module:loaders~ProgressInfo}
     * @readonly
     */
    get progressInfo() {
        let c = this._complete ? 1 : 0;
        return {total: 1, complete: c, progress: c};
    }

    /**
     * @type {boolean}
     */
    get complete() {
        return !!this._complete;
    }

    //noinspection JSAnnotator
    set complete(val) {
        if (this._complete) return; // only once
        this._complete = !!val;
        if (this._complete) {
            // for loaders compatibility
            this.emit('progress', this.progressInfo);

            if (this._status == 'error') {
                this._dispatchError();
            }

            if (this._status == 'success') {
                this._dispatchSuccess();
            }

            this._dispatchComplete();
        }
    }

    _dispatchComplete(){
        /**
         * @event module:media.module:loaders.QueueItem#complete
         * @property {String} status - Completion status [success|error]
         * @property {String} message - Status message [OK|ErrorMessage]
         */
        this.emit('complete', {status: this._status, message: this._statusMessage});
    }

    _dispatchSuccess(){
        /**
         * @event module:media.module:loaders.QueueItem#success
         */
        this.emit('success', {status: this._status, message: this._statusMessage});
    }

    _dispatchError(){
        /**
         * @event module:media.module:loaders.QueueItem#error
         */
        this.emit('error', {status: this._status, message: this._statusMessage});
    }

    /**
     * @param {...*} params Параметры конструктора
     * @returns {*} Реализация загрузчика данных, зависит от типа. Например, тег IMG или AUDIO или объект XMLHTTPRequest и т.п.
     * @ignore
     */
    prepareData(...params) {
        return params.shift();
    }

    /**
     * @param {...*} params Параметры конструктора
     * @returns {*} Уникальный идентификатор данных. Например URL.
     * @ignore
     */
    prepareKey(...params) {
        return params.shift();
    }

    parseData() {
        if (this.parser instanceof Function) {
            try {
                this.data = this.parser(this.data);
            }
            catch (e) {
                this._status = 'error';
                this._statusMessage = `Error parsing data ${this.key}: ${e.message}`;
                this.data = null;
            }
        }
    }

    cached(key = this.key){
        return CACHE.get(key);
    }

    cache(key = this.key){
        if (this.data) {
            CACHE.put(key, this.data, this.name);
        }
        else {
            CACHE.remove(key);
        }
    }

    /**
     * Начать загрузку
     * @param {Boolean} cache - Использовать кеш загрузок или перезагрузить в любом случае
     */
    load(cache = true) {
        if (cache) {
            let data = this.cached();
            if (data) {
                this.data = data;
                this.completeLoad(cache);
            }
        }
    }

    completeLoad(cache = true){
        this.parseData();
        if (cache) {
            this.cache();
        }
        this._status = this._status || 'success';
        this._statusMessage = this._statusMessage || (this._status == 'success' ? 'OK' : 'Unknown error');
        this.complete = true;
    }

    /**
     * Загрузить повторно. Если при предыдущей загрузке было использовано кеширование, надо почистить запись в кеше, метод сам этого не делает.
     */
    reload() { // not tested well!
        this._complete = false;
        this._status = null;
        this._statusMessage = null;
        this.load(false);
    }
}

export default QueueItem;